import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";
import UserMenu from "./UserMenu";

const LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_nomad-wheels-8/artifacts/pqjvkrx0_ChatGPT%20Image%20Sep%2014%2C%202026%20at%2010_25_01%20PM.png";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/tours", label: "Tours" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" }
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);
  const isHome = location.pathname === "/";
  const solid = true; // Always solid — no dark hero on any page anymore

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? "bg-[#F7F3EA]/95 backdrop-blur border-b border-[#E5DDCB] shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="On Wheels"
            className="logo-nobg h-10 sm:h-12 md:h-16 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `nav-link text-sm font-medium tracking-wide ${
                  solid
                    ? isActive
                      ? "text-[#1F4D3A] active"
                      : "text-[#4A3728] hover:text-[#1F4D3A]"
                    : isActive
                    ? "text-white active"
                    : "text-[#F5F0E8] hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+919606094060"
            className={`flex items-center gap-2 text-sm font-medium ${solid ? "text-[#4A3728]" : "text-white"}`}
            data-testid="navbar-phone-link"
          >
            <Phone className="w-4 h-4" />
            +91 96060 94060
          </a>
          <UserMenu />
          <Link to="/contact">
            <Button className="btn-primary bg-[#1F4D3A] hover:bg-[#2D6B4F] text-[#F7F3EA] rounded-full px-6">
              Plan a Trip
            </Button>
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className={`md:hidden p-2 rounded-md ${solid ? "text-[#1F4D3A]" : "text-white"}`}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#F7F3EA] border-t border-[#E5DDCB] px-6 py-6 space-y-4">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `block text-base font-medium ${isActive ? "text-[#1F4D3A]" : "text-[#4A3728]"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <div className="pt-2">
            <UserMenu compact />
          </div>
          <Link to="/contact">
            <Button className="w-full bg-[#1F4D3A] hover:bg-[#2D6B4F] text-[#F7F3EA] rounded-full mt-3">
              Plan a Trip
            </Button>
          </Link>
        </div>
      )}
    </header>
  );
}
